import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnuncios } from '../hooks/useAnuncios';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Loading from '../components/common/Loading';
import AnuncioCard from '../components/anuncios/AnuncioCard';

interface DashboardStats {
  totalAnuncios: number;
  anunciosAtivos: number;
  totalVisualizacoes: number;
  totalOfertas: number;
  ofertasPendentes: number;
  avaliacaoMedia: number;
}

export default function Dashboard() {
  const { usuario } = useAuth();
  const { meusAnuncios, isLoading: loading, carregarMeusAnuncios: fetchMeusAnuncios } = useAnuncios();
  const [stats, setStats] = useState<DashboardStats>({
    totalAnuncios: 0,
    anunciosAtivos: 0,
    totalVisualizacoes: 0,
    totalOfertas: 0,
    ofertasPendentes: 0,
    avaliacaoMedia: 0
  });

  useEffect(() => {
    fetchMeusAnuncios();
  }, []);

  useEffect(() => {
    if (meusAnuncios.length > 0) {
      const ativos = meusAnuncios.filter(a => a.status === 'ativo').length;
      const visualizacoes = meusAnuncios.reduce((acc, a) => acc + (a.visualizacoes || 0), 0);
      
      setStats({
        totalAnuncios: meusAnuncios.length,
        anunciosAtivos: ativos,
        totalVisualizacoes: visualizacoes,
        totalOfertas: 12, // Mock
        ofertasPendentes: 3, // Mock
        avaliacaoMedia: 4.8 // Mock
      });
    }
  }, [meusAnuncios]);

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Olá, {usuario?.nome?.split(' ')[0]}! 👋
          </h1>
          <p className="text-gray-600 mt-2">
            Bem-vindo ao seu painel de vendedor
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard 
            title="Anúncios" 
            value={stats.totalAnuncios} 
            icon="📋"
            color="bg-blue-500"
          />
          <StatCard 
            title="Ativos" 
            value={stats.anunciosAtivos} 
            icon="✅"
            color="bg-green-500"
          />
          <StatCard 
            title="Visualizações" 
            value={stats.totalVisualizacoes} 
            icon="👁️"
            color="bg-purple-500"
          />
          <StatCard 
            title="Ofertas" 
            value={stats.totalOfertas} 
            icon="💰"
            color="bg-yellow-500"
          />
          <StatCard 
            title="Pendentes" 
            value={stats.ofertasPendentes} 
            icon="⏳"
            color="bg-orange-500"
          />
          <StatCard 
            title="Avaliação" 
            value={stats.avaliacaoMedia.toFixed(1)} 
            icon="⭐"
            color="bg-pink-500"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Ações Rápidas
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickAction 
              to="/criar-anuncio" 
              icon="➕" 
              label="Novo Anúncio" 
            />
            <QuickAction 
              to="/meus-anuncios" 
              icon="📝" 
              label="Meus Anúncios" 
            />
            <QuickAction 
              to="/chat" 
              icon="💬" 
              label="Mensagens" 
              badge={3}
            />
            <QuickAction 
              to="/planos" 
              icon="⭐" 
              label="Planos Premium" 
            />
          </div>
        </div>

        {/* Recent Listings */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Anúncios Recentes
            </h2>
            <Link 
              to="/meus-anuncios" 
              className="text-green-600 hover:text-green-700 font-medium"
            >
              Ver todos →
            </Link>
          </div>
          
          {meusAnuncios.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {meusAnuncios.slice(0, 3).map(anuncio => (
                <AnuncioCard key={anuncio.id} anuncio={anuncio} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                Você ainda não tem anúncios
              </p>
              <Link 
                to="/criar-anuncio"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Criar Primeiro Anúncio
              </Link>
            </div>
          )}
        </div>

        {/* Performance Chart Placeholder */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Desempenho (Últimos 30 dias)
          </h2>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-4xl mb-2">📊</p>
              <p className="text-gray-500">
                Gráfico de visualizações e ofertas
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StatCard({ title, value, icon, color }: { 
  title: string; 
  value: number | string; 
  icon: string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white text-xl mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
}

function QuickAction({ to, icon, label, badge }: { 
  to: string; 
  icon: string; 
  label: string;
  badge?: number;
}) {
  return (
    <Link 
      to={to}
      className="relative flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
    >
      <span className="text-3xl mb-2">{icon}</span>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
    </Link>
  );
}
